import { useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ApplicationContext from "../context/application/ApplicationContext";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import {
  isClientIdentificationActive,
  TENANT_REQUIREMENTS,
  haveValue,
  isValidEmail,
  toast,
} from "../helper/common";
import {
  getInitials,
  resolveParticipantName,
} from "../helper/applicationHelpers";
import SkorProtectSelector from "./SkorProtectSelector/SkorProtectSelector";
import SkorProtectBadge from "./SkorProtectBadge/SkorProtectBadge";
import CreateGroupScreen from "./CreateGroupScreen/CreateGroupScreen";
import BeforeSharingModal, {
  APPLICANT_QUESTIONS_STEP,
} from "./TenantRequirements/BeforeSharingModal";
import axiosInstance from "../services/authWrapper";
import constants from "../Utils/constants";
import {
  Avatar,
  Button,
  Icon,
  Modal,
  OptionCard,
  SkeletonList,
} from "./design";
import "./InvitationShareModal.css";

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const resolveGuarantorName = (entry) => {
  if (!entry || typeof entry === "string") return "";
  const guarantor =
    entry.guarantor_id ?? entry.gurantor_id ?? entry.guarantor ?? entry;
  const name =
    entry.gurantor_name ??
    entry.guarantor_name ??
    `${guarantor?.fname ?? guarantor?.firstname ?? ""} ${
      guarantor?.lname ?? guarantor?.lastname ?? ""
    }`.trim();
  return (
    name ||
    entry.gurantor_email ||
    entry.guarantor_email ||
    guarantor?.email ||
    ""
  );
};

const getParticipantGuarantors = (participant, user, isMe) => {
  const candidates = [
    participant?.id?.gurantor_id,
    participant?.tenant_info?.gurantor_id,
    isMe ? user?.gurantor_added_id : null,
    participant?.guarantors,
    participant?.guarantorIds,
  ];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const guarantors = candidate
      .map((entry) => ({ entry, name: resolveGuarantorName(entry) }))
      .filter(({ name }) => name);
    if (guarantors.length > 0) return guarantors;
  }
  return [];
};

const isSharedWithLandlord = (app, landlordId) => {
  if (!landlordId) return false;
  const extracted_landlord_id = landlordId?.split?.("_")?.[0] ?? landlordId;
  const inShareRequests = (app.shareRequests ?? []).some(
    (entry) => entry?.landlord_id?._id === extracted_landlord_id
  );
  if (inShareRequests) return true;
  const inSharedProfiles = (app.sharedProfiles ?? []).some(
    (sp) => sp?.id?.landlordid?._id === extracted_landlord_id
  );
  return inSharedProfiles;
};

/* ── SCREEN 1 – choice ── */
const ChoiceScreen = ({
  landlordData,
  applicationToShare,
  applicationOnly,
  hideIndividualOption,
  onSelectIndividual,
  onSelectApplication,
  onSelectCreate,
  onClose,
  processing,
  individualSharing,
  _t,
  user,
}) => {
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord", "Landlord");
  const individualProfileTitle =
    typeof user?.application_title === "string" && user.application_title.trim()
      ? user.application_title.trim()
      : _t("myIndividualProfile", "My individual profile");

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!processing}
      tone="ink"
      eyebrow={
        <>
          <Avatar name={landlordName} size="lg" tone="lime" />
          <span className="isf-eyebrow">
            <span className="isf-eyebrow__name">{landlordName}</span>
            <span className="isf-eyebrow__sub">
              {_t(
                "hasInvitedYouToShareYourProfile",
                "has invited you to share your profile"
              )}
            </span>
          </span>
        </>
      }
      title={_t(
        "whatWouldYouLikeToShare",
        "What would you like to share with this landlord?"
      )}
    >
      <div className="ds-option-list">
        {/* Individual profile */}
        {!applicationOnly && !hideIndividualOption && (
          <OptionCard
            icon={<Icon name="user" />}
            title={
              individualSharing
                ? _t("sharing", "Sharing...")
                : individualProfileTitle
            }
            description={_t(
              "shareOnlyYourOwnProfile",
              "Share only your own profile."
            )}
            onClick={onSelectIndividual}
            busy={individualSharing}
          />
        )}

        {/* Latest application */}
        {(applicationToShare ? [applicationToShare] : []).map((app) => {
          const participants = app.participants ?? [];
          return (
            <OptionCard
              key={app._id}
              variant="highlight"
              iconTone="ink"
              icon={<Icon name="users" />}
              title={app.title}
              busy={processing}
              onClick={() => !processing && onSelectApplication(app)}
            >
              <span className="isf-chips-row">
                {participants.map((p, i) => {
                  const name = resolveParticipantName(p);
                  const isMe =
                    p.email?.toLowerCase() === user?.email?.toLowerCase();
                  const guarantors = getParticipantGuarantors(p, user, isMe);
                  return (
                    <span
                      key={i}
                      className="isf-chip-stack"
                      aria-label={
                        guarantors.length > 0
                          ? `${isMe ? _t("you", "You") : name}, ${guarantors.length} ${_t("guarantor", "guarantor")}`
                          : isMe
                            ? _t("you", "You")
                            : name
                      }
                    >
                      <span
                        className={`isf-chip${isMe ? " isf-chip-me" : ""}`}
                        title={name}
                      >
                        {isMe ? _t("you", "You") : getInitials(name)}
                      </span>
                      {guarantors.slice(0, 3).map(({ entry, name }, j) => (
                        <span
                          key={
                            entry?._id ??
                            entry?.guarantor_id?._id ??
                            entry?.gurantor_id?._id ??
                            `${name}-${j}`
                          }
                          className="isf-chip isf-chip-guarantor"
                          title={`${name} · ${_t("guarantor", "Guarantor")}`}
                        >
                          {getInitials(name)}
                        </span>
                      ))}
                      {guarantors.length > 3 && (
                        <span
                          className="isf-chip isf-chip-guarantor isf-chip-overflow"
                          title={`${guarantors.length - 3} ${_t("more", "more")}`}
                        >
                          +{guarantors.length - 3}
                        </span>
                      )}
                    </span>
                  );
                })}
              </span>
            </OptionCard>
          );
        })}

        {/* Create new group */}
        <OptionCard
          variant="dashed"
          iconTone="muted"
          icon={<Icon name="plus" />}
          title={_t("createANewGroup", "Create a new group")}
          description={_t(
            "inviteYourFlatmatesByEmail",
            "Invite your flatmates by email."
          )}
          onClick={onSelectCreate}
          disabled={processing}
        />
      </div>
    </Modal>
  );
};

/* ── SCREEN 1b – individual profile + skor protect ── */
const IndividualScreen = ({
  landlordData,
  onBack,
  onConfirm,
  processing,
  _t,
}) => {
  const [skorProtect, setSkorProtect] = useState(true);
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord");

  return (
    <Modal
      open
      onClose={onBack}
      dismissible={false}
      align="start"
      onBack={onBack}
      backLabel={_t("back")}
      title={_t("shareYourIndividualProfile")}
      description={`${_t("with")} ${landlordName}`}
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={onBack}
            disabled={processing}
          >
            {_t("back")}
          </Button>
          <Button
            size="lg"
            onClick={() => onConfirm(skorProtect)}
            loading={processing}
          >
            {_t("shareProfile")}
          </Button>
        </>
      }
    >
      <div className="isf-modal-body">
        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>
    </Modal>
  );
};

/* ── SCREEN 2 – join group ── */
export { resolveCreatorEmail };
const JoinGroupScreen = ({
  app,
  landlordData,
  onBack,
  onClose,
  onAccept,
  onDecline,
  processing,
  user,
  _t,
}) => {
  const creatorName = resolveCreatorName(app.created_by);
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const participants = app.participants ?? [];
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    _t("theLandlord", "the landlord");

  const getRole = (p) => {
    const isOwner =
      creatorEmail && p.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwner) return "owner";
    const isMe = p.email?.toLowerCase() === user?.email?.toLowerCase();
    if (isMe) return "pending";
    return p.consent_status ?? "pending";
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "owner":
        return _t("groupOwner", "Group owner");
      case "pending":
        return _t("pendingAcceptance", "Pending acceptance");
      case "accepted":
        return _t("accepted", "Accepted");
      case "rejected":
        return _t("rejected", "Rejected");
      default:
        return role;
    }
  };

  return (
    <Modal
      open
      onClose={onClose ?? onDecline}
      dismissible={false}
      align="start"
      onBack={onBack || undefined}
      backLabel={_t("back", "Back")}
      title={`${_t("join")} ${creatorName}'s ${_t("group")}`}
      description={
        landlordData ? `${_t("with", "with")} ${landlordName}` : undefined
      }
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={onDecline}
            disabled={processing}
          >
            {_t("decline", "Decline")}
          </Button>
          <Button size="lg" onClick={onAccept} loading={processing}>
            {_t("acceptAndJoin", "Accept & join")}
          </Button>
        </>
      }
    >
      <div className="isf-modal-body">
        <div className="isf-members-card">
          <div className="isf-members-card-header">
            <span className="isf-members-label">
              {_t("groupMembers", "Group members")}
            </span>
            {app.skor_protect && (
              <>
                <span
                  data-tooltip-id="isf-sp-badge-tooltip"
                  className="d-flex align-items-center"
                >
                  <SkorProtectBadge />
                </span>
                <ReactTooltip
                  id="isf-sp-badge-tooltip"
                  place="bottom"
                  className="isf-sp-tooltip-dark"
                  render={() => (
                    <span className="isf-sp-tooltip-text">
                      {participants?.[0]?.name +
                        " " +
                        _t("skorProtectGroupTooltip")}
                    </span>
                  )}
                />
              </>
            )}
          </div>
          <div className="isf-members-list">
            {participants.map((p, i) => {
              const name = resolveParticipantName(p);
              const isMe =
                p.email?.toLowerCase() === user?.email?.toLowerCase();
              const role = getRole(p);
              return (
                <div key={i} className="isf-member-row">
                  <Avatar name={isMe ? "You" : name} />
                  <div className="isf-member-info">
                    <span className="isf-member-name">
                      {isMe ? _t("you", "You") : name}
                    </span>
                    <span className="isf-member-role">
                      {getRoleLabel(role)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="isf-join-disclaimer">
          {landlordData
            ? _t("byAcceptingYourProfileWillBeShared")
            : _t("byAcceptingYouWillJoinGroup")}
        </p>
      </div>
    </Modal>
  );
};

/* ── MAIN COMPONENT ── */
const InvitationShareModal = ({
  isOpen,
  landlordId, // cookies.requestProfileLink — may become undefined after cookies clear
  propertyId, // cookies.property_id
  onClose,
  onClearCookies,
  onNeedsConsent,
  onDone,
  // Website-entry reuse (cases 3-4): drive the same modal from a landlord
  // request loaded via API instead of a landlord link. When requestOverride is
  // provided, the link-registration flow is skipped.
  requestOverride,
  applicationOverride,
  closeBehavior,
  onCreateNew,
  landlordDataOverride,
  compactChoice,
  // Tenant requirements gate (onboarding and the landlord link on /home):
  // when true, every share action checks the landlord's requirements first
  // and opens
  // BeforeSharingModal (household and pets, KYC-AML form) when a step is
  // required before acceptLandlordRequest.
  enableViviaGate = false,
  // Called when the gated identification form is submitted (POST succeeded).
  // Lets the parent refresh the tenant after the share completes.
  onViviaSubmitted,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const { getLandlordsConsentInfo, loadSharedProfiles, declinePendingShare } =
    useContext(TenantContext);
  const {
    pendingApplications,
    myApplications,
    createApplication,
    consentApplication,
    loadApplicationsData,
    openLandlordLink,
    loadLandlordRequests,
    acceptLandlordRequest,
  } = useContext(ApplicationContext);

  const currentLang = localStorage.getItem("i18nextLng").toLowerCase() || "en";

  const defaultTitle =
    currentLang === "es"
      ? `Aplicación de ${user?.fname}`
      : `${user?.fname}'s Application`;

  const [step, setStep] = useState("choice");
  const [selectedApp, setSelectedApp] = useState(null);
  const [landlordData, setLandlordData] = useState(null);
  const [landLordLoading, setLandlordLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [individualSharing, setIndividualSharing] = useState(false);
  // Deferred share waiting on the before-sharing steps. Stays set while
  // the form is open; cancel blocks the share, submit runs action.
  const [pendingVivia, setPendingVivia] = useState(null);
  // Snapshot IDs so parent cookie-clearing doesn't affect in-flight actions
  const capturedLandlordId = useRef(null);
  const requestId = useRef(null);
  const fetchedRef = useRef(false);
  // true when user arrived via the signup flow (tenantSignupFlow cookie was set)
  const isSignupFlowRef = useRef(false);
  const [signupCookies, , removeSignupCookie] = useCookies([
    "tenantSignupFlow",
  ]);

  useEffect(() => {
    if (isOpen && requestOverride && !fetchedRef.current) {
      // API-driven mode: request already exists, no link registration needed.
      fetchedRef.current = true;
      requestId.current = requestOverride._id ?? null;
      const landlord = requestOverride.landlord_id ?? {};
      const openedWorkspaceRef = requestOverride.workspace_id;
      console.log("[signup-flow]", "landlord share modal opened (API mode)", {
        requestId: requestOverride._id ?? null,
        landlord:
          `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
          landlord.email ||
          null,
        hasApplication: Boolean(applicationOverride?._id),
        closeBehavior: closeBehavior ?? "share",
        clientIdentificationRule: {
          request: isClientIdentificationActive(requestOverride),
          landlord: isClientIdentificationActive(landlord),
          workspace:
            openedWorkspaceRef && typeof openedWorkspaceRef === "object"
              ? isClientIdentificationActive(openedWorkspaceRef)
              : null,
        },
      });
      setLandlordData(
        landlordDataOverride ?? {
          fname_companyName: landlord.fname_companyName,
          lname: landlord.lname,
          email: landlord.email,
          insurance_service_status: landlord.insurance_service_status,
          affidavit_activated: landlord.affidavit_activated,
          client_identification_rule:
            isClientIdentificationActive(landlord) ||
            isClientIdentificationActive(requestOverride),
        }
      );
      setLandlordLoading(false);
      return;
    }
    if (isOpen && landlordId && !fetchedRef.current) {
      fetchedRef.current = true;
      // Snapshot before cookies are cleared
      capturedLandlordId.current = landlordId;
      const capturedPropertyId = propertyId;
      // Capture signup-flow flag before clearing cookies
      isSignupFlowRef.current = haveValue(signupCookies.tenantSignupFlow);

      setLandlordLoading(true);

      Promise.all([
        getLandlordsConsentInfo(landlordId),
        openLandlordLink(landlordId, capturedPropertyId),
        loadApplicationsData(),
      ])
        .then(([consentRes, linkRes]) => {
          setLandlordData(consentRes?.data ?? consentRes);
          // Store the request ID returned by the API
          requestId.current =
            linkRes?.data?._id ??
            linkRes?.success_res?._id ??
            linkRes?._id ??
            null;
          // Clear cookies now that the link has been registered
          onClearCookies();
          removeSignupCookie("tenantSignupFlow", { path: "/" });
          // Refresh landlord requests to surface any notifications
          return loadLandlordRequests();
        })
        .catch(() => {
          // Still clear cookies on failure to avoid re-triggering
          onClearCookies();
          removeSignupCookie("tenantSignupFlow", { path: "/" });
        })
        .finally(() => setLandlordLoading(false));
    }
    if (!isOpen) {
      fetchedRef.current = false;
      setStep("choice");
      setSelectedApp(null);
      setIndividualSharing(false);
      setPendingVivia(null);
      requestId.current = null;
      capturedLandlordId.current = null;
      isSignupFlowRef.current = false;
    }
  }, [isOpen, landlordId]);

  // Signup flow: filter to apps already shared with this landlord (flatmate added you).
  // Already-logged-in flow: skip the landlord filter, just surface the latest pending app.
  const pendingAppsForLandlord = isSignupFlowRef.current
    ? (() => {
        const filtered = (pendingApplications ?? []).filter((app) =>
          isSharedWithLandlord(app, capturedLandlordId.current ?? landlordId)
        );

        filtered.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        return filtered.slice(0, 1);
      })()
    : (() => {
        const sorted = [...(pendingApplications ?? [])].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        return sorted.slice(0, 1);
      })();

  const latestOwnedApplication = [...(myApplications ?? [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  )[0];
  const applicationToShare = [
    ...(myApplications ?? []),
    ...pendingAppsForLandlord,
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  const onboardingApplicationOnly =
    isSignupFlowRef.current && Boolean(latestOwnedApplication?._id);
  // When driven by an API request with an explicit application (or individual),
  // use exactly that primary option plus Create new – no extra cards.
  const hasApplicationOverride = requestOverride !== undefined;
  const displayedApplication = hasApplicationOverride
    ? (applicationOverride ?? null)
    : onboardingApplicationOnly
      ? latestOwnedApplication
      : applicationToShare;
  const hideIndividualOption = hasApplicationOverride
    ? Boolean(applicationOverride?._id)
    : false;

  const handleClose = (markDeclined = false, declineSource = null) => {
    if (markDeclined) {
      markShareDeclined(declineSource ?? "choice-close");
    }
    setStep("choice");
    setSelectedApp(null);
    setPendingVivia(null);
    onClose();
  };

  // Records a dismissed share decision so the forensic resolver never
  // confirms it. Resolves the landlord from the API request (queue mode) or
  // the landlord link (link mode); no-op when nothing pending matches.
  const markShareDeclined = (source) => {
    const overrideLandlord = requestOverride?.landlord_id;
    const overrideWorkspace = requestOverride?.workspace_id;
    const [linkLandlordId, linkWorkspaceUuid] = String(
      capturedLandlordId.current ?? landlordId ?? ""
    ).split("_");
    const payload = { source };
    if (overrideLandlord?._id ?? overrideLandlord) {
      payload.landlordId = overrideLandlord?._id ?? overrideLandlord;
    } else if (linkLandlordId) {
      payload.landlordId = linkLandlordId;
    } else if (landlordData?.email) {
      payload.landlordEmail = landlordData.email;
    }
    const workspaceRef = overrideWorkspace ?? null;
    if (workspaceRef?._id ?? workspaceRef) {
      payload.workspaceId = workspaceRef?._id ?? workspaceRef;
    } else if (linkWorkspaceUuid) {
      payload.workspaceId = linkWorkspaceUuid;
    }
    if (!payload.landlordId && !payload.landlordEmail) return;
    declinePendingShare(payload);
  };

  /* returns true if landlord requires consent the tenant hasn't given yet */
  const consentRequired = () => {
    if (!landlordData) return false;
    const insuranceEnabled = landlordData?.insurance_service_status;
    const affidavitEnabled = landlordData?.affidavit_activated;
    const hasInsuranceConsent =
      haveValue(user?.consents?.gdpr_consent) &&
      haveValue(user?.consents?.skor_consent);
    const hasAffidavitConsent = haveValue(user?.consents?.declaration_consent);
    return (
      (insuranceEnabled && !hasInsuranceConsent) ||
      (affidavitEnabled && !hasAffidavitConsent)
    );
  };

  /*
   * Gate every share action behind consent.
   * If consent is needed: close this modal, let parent show ConsentStepModal
   * with the captured action as the post-consent callback.
   * If consent is not needed: run action directly.
   */
  const withConsentGate = async (action) => {
    if (consentRequired()) {
      if (typeof onNeedsConsent !== "function") {
        toast.error(
          _t("consentRequired", "Consent is required before sharing")
        );
        return;
      }
      onNeedsConsent(landlordData, action);
      // API-driven queues keep this request active while the parent collects consent.
      if (!requestOverride) handleClose();
      return;
    }
    await action();
  };

  /* Tenant requirements pre-share gate (onboarding landlord queue only). Runs BEFORE the
   * consent gate so the identification form opens while this modal is still
   * visible: consent may hide this modal via onNeedsConsent in the parent.
   * Cancel blocks the share and stays on the choice screen – except when the
   * gate was entered from the choice X (fromChoiceClose), where cancelling
   * with required steps pending closes the modal without sharing so the
   * parent advances (invitation stays pending). */
  const isViviaGateActive = Boolean(enableViviaGate);

  const resolveFullApplication = (appOrId) => {
    const id = typeof appOrId === "string" ? appOrId : (appOrId?._id ?? null);
    if (!id) return typeof appOrId === "object" ? appOrId : null;
    const pool = [...(myApplications ?? []), ...(pendingApplications ?? [])];
    // applicationOverride in onboarding only carries _id/title/participants,
    // so look up the loaded application for the client identification flag.
    return (
      pool.find((a) => a?._id?.toString?.() === id?.toString?.()) ??
      (typeof appOrId === "object" ? appOrId : null)
    );
  };

  const getViviaRequirement = (applicationOrNull) => {
    const consentStatus = user?.consents?.client_identification_consent?.status;
    if (consentStatus === constants.ACCEPTED)
      return { required: false, reason: "consent-accepted" };
    const fullApp = applicationOrNull
      ? resolveFullApplication(applicationOrNull)
      : null;
    const appRequired = fullApp?.client_identification_required === true;
    const landlord = requestOverride?.landlord_id ?? {};
    const workspaceRef = requestOverride?.workspace_id;
    // Note: getLandlordRequestsByEmail populates landlord_id with name fields
    // only and leaves workspace_id unpopulated, so the landlord-side rule is
    // usually absent here. The tenant's own workspace carries the invite
    // workspace rule (set at signup), same source PostApplicationModals and
    // skorList use — that is the primary signal in this flow.
    const userWorkspaceRequired = isClientIdentificationActive(
      user?.workspaceId
    );
    const landlordRequired =
      isClientIdentificationActive(requestOverride) ||
      isClientIdentificationActive(landlord) ||
      (workspaceRef && typeof workspaceRef === "object"
        ? isClientIdentificationActive(workspaceRef)
        : false) ||
      isClientIdentificationActive(landlordData) ||
      userWorkspaceRequired;
    const required = appRequired || landlordRequired;
    return {
      required,
      reason: !required
        ? "not-required"
        : appRequired && landlordRequired
          ? "both"
          : appRequired
            ? "app-flag"
            : "landlord-rule",
      appRequired,
      landlordRequired,
      userWorkspaceRequired,
      appFlagUnknown: Boolean(applicationOrNull) && !fullApp,
    };
  };

  const withViviaGate = async (
    afterVivia,
    { application = null, appId = null, shareKind, fromChoiceClose = false }
  ) => {
    if (!isViviaGateActive) {
      await afterVivia();
      return;
    }
    // Loader while the landlord's steps are checked (the modal looked frozen)
    setProcessing(true);
    const reqId = requestId.current ?? null;
    const appRef = application ?? (appId ? { _id: appId } : null);
    const resolvedApp = appRef
      ? (resolveFullApplication(appRef) ?? appRef)
      : null;
    const check = getViviaRequirement(resolvedApp);
    console.log("[signup-flow]", "client identification check before share", {
      reqId,
      shareKind,
      appId: resolvedApp?._id ?? appId ?? null,
      appRequired: check.appRequired ?? false,
      landlordRequired: check.landlordRequired ?? false,
      userWorkspaceRequired: check.userWorkspaceRequired ?? false,
      appFlagUnknown: check.appFlagUnknown ?? false,
      consentStatus:
        user?.consents?.client_identification_consent?.status ?? null,
      required: check.required,
      reason: check.reason,
      overrideMode: Boolean(requestOverride),
    });
    const workspaceRef = requestOverride?.workspace_id;
    const workspace =
      workspaceRef && typeof workspaceRef === "object"
        ? workspaceRef
        : (user?.workspaceId ?? null);
    const resolvedAppId = resolvedApp?._id ?? appId ?? null;
    // Landlord link mode: the link is "landlordId_workspaceUuid"
    const [linkLandlordId, linkWorkspaceUuid] = String(
      capturedLandlordId.current ?? landlordId ?? ""
    ).split("_");
    const contextRes = await axiosInstance
      .get(`${process.env.REACT_APP_SERVER_URL}/tenants/requirements-context`, {
        params: {
          application_id: resolvedAppId ?? undefined,
          landlord_id: requestOverride
            ? (requestOverride.landlord_id?._id ?? requestOverride.landlord_id)
            : linkLandlordId || undefined,
          workspace_id: requestOverride
            ? (workspaceRef?._id ?? workspaceRef ?? undefined)
            : linkWorkspaceUuid || undefined,
          include_tenant_workspace: "true",
        },
      })
      .catch(() => null);
    const context = contextRes?.data?.success_res ?? {};
    const requirements = context.requirements ?? {};
    const household = context.household_pets ?? null;
    // The household questions the landlords ticked (all when not sent)
    const householdQuestions = context.household_questions ?? null;
    const applicantQuestions = context.applicant_questions ?? null;
    const minAdults = context.min_adults ?? 1;
    const memberStatuses = context.members ?? [];

    // Steps the landlord asks for, in the order shown in the modal
    const steps = [];
    if (requirements[TENANT_REQUIREMENTS.HOUSEHOLD]?.active) {
      steps.push({
        key: TENANT_REQUIREMENTS.HOUSEHOLD,
        requiredBeforeSharing:
          requirements[TENANT_REQUIREMENTS.HOUSEHOLD].required_before_sharing,
        // Done when every question asked now is answered
        done: context.household_complete ?? Boolean(household),
      });
    }
    // LinkedIn and the workspace's own questions. Required before sharing
    // when the workspace ticked Required on Applicant questions.
    if (applicantQuestions) {
      steps.push({
        key: APPLICANT_QUESTIONS_STEP,
        requiredBeforeSharing: applicantQuestions.questions?.required === true,
        done: applicantQuestions.complete === true,
      });
    }
    const identification =
      requirements[TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION];
    // Not asked when the workspace switched KYC off (requirements_config.kyc),
    // even if an older flag still says it is required
    if (
      !context.kyc_disabled &&
      (identification?.active || check.appRequired || check.landlordRequired)
    ) {
      steps.push({
        key: TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION,
        // Without tenant_requirements (legacy flag) the form stays required
        requiredBeforeSharing: identification?.active
          ? identification.required_before_sharing
          : true,
        // Fresh from the backend: the user object is stale after signing
        done:
          context.identification_signed ??
          user?.consents?.client_identification_consent?.status ===
            constants.ACCEPTED,
      });
    }

    // Show the steps while any is open; "Required before sharing" decides
    // whether closing shares. Nothing open: share straight away.
    if (steps.every((item) => item.done)) {
      setProcessing(false);
      console.log("[signup-flow]", "no open step before sharing", {
        reqId,
        shareKind,
        appId: resolvedAppId,
        steps,
      });
      await afterVivia();
      return;
    }
    // requestOverride.email is the tenant's own email (the request is keyed
    // by it), never the landlord's — so it must not be used as landlordEmail.
    const landlordEmail =
      landlordData?.email ?? requestOverride?.landlord_id?.email ?? null;
    console.log("[signup-flow]", "opening before-sharing steps", {
      reqId,
      shareKind,
      appId: resolvedAppId,
      steps,
    });
    setProcessing(false);
    setPendingVivia({
      action: afterVivia,
      shareKind,
      fromChoiceClose,
      appId: resolvedAppId,
      appTitle: resolvedApp?.title ?? null,
      memberStatuses,
      landlordEmail,
      workspace,
      steps,
      household,
      householdQuestions,
      applicantQuestions,
      minAdults,
    });
  };

  // Runs the share that was waiting on the before-sharing steps
  const runPendingShare = async () => {
    const deferred = pendingVivia?.action;
    // Run the deferred share in this live instance. Never reload the user
    // here: a reload remounts the signup step and this continuation would run
    // in a dead closure (share succeeds server-side, UI state updates lost).
    // The parent reloads after advancing the queue (see handleLandlordShared).
    setPendingVivia(null);
    setProcessing(true);
    try {
      await deferred?.();
    } finally {
      setProcessing(false);
    }
  };

  const hasRequiredStepPending = (steps) =>
    steps.some((item) => item.requiredBeforeSharing && !item.done);

  // The modal stays open while other steps are open; the share runs when the
  // last step is completed, or on close once the required steps are done.
  const handleStepDone = async (stepKey) => {
    if (stepKey === TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION) {
      onViviaSubmitted?.();
    }
    const steps = (pendingVivia?.steps ?? []).map((item) =>
      item.key === stepKey ? { ...item, done: true } : item
    );
    const allDone = steps.every((item) => item.done);
    console.log("[signup-flow]", "before-sharing step completed", {
      reqId: requestId.current ?? null,
      stepKey,
      allDone,
      requiredPending: hasRequiredStepPending(steps),
    });
    if (!allDone) {
      setPendingVivia((current) => (current ? { ...current, steps } : current));
      return;
    }
    await runPendingShare();
  };

  const handleBeforeSharingClose = async () => {
    const steps = pendingVivia?.steps ?? [];
    if (hasRequiredStepPending(steps)) {
      if (pendingVivia?.fromChoiceClose) {
        // X on the choice screen opened these steps: closing with a blocking
        // step still open shares nothing and lets the parent proceed next
        // (invitation stays pending for Share profile).
        console.log(
          "[signup-flow]",
          "before-sharing closed after X, proceeding without sharing",
          {
            reqId: requestId.current ?? null,
          }
        );
        handleClose(true, "before-sharing-close");
        return;
      }
      console.log("[signup-flow]", "before-sharing closed, share cancelled", {
        reqId: requestId.current ?? null,
      });
      setPendingVivia(null);
      return;
    }
    console.log("[signup-flow]", "before-sharing closed, sharing", {
      reqId: requestId.current ?? null,
    });
    await runPendingShare();
  };

  /* Individual profile → respond to landlord request with optional skor_protect */
  const handleIndividual = async (skorProtect = true, opts = {}) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: individual selected", {
      reqId,
      overrideMode: Boolean(requestOverride),
    });
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const doShare = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId, null, skorProtect);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error(_t("failedToShareProfile"));
      } finally {
        setProcessing(false);
      }
    };
    await withViviaGate(
      async () => {
        await withConsentGate(doShare);
      },
      {
        application: null,
        appId: null,
        shareKind: "individual",
        fromChoiceClose: opts.fromChoiceClose === true,
      }
    );
  };

  const handleApplication = async (application, opts = {}) => {
    if (!requestId.current || !application?._id) return;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: application selected", {
      reqId,
      appId: application._id,
      overrideMode: Boolean(requestOverride),
    });
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const doShare = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId, application._id);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error(_t("failedToShareProfile"));
      } finally {
        setProcessing(false);
      }
    };
    await withViviaGate(
      async () => {
        await withConsentGate(doShare);
      },
      {
        application,
        appId: application._id,
        shareKind: "application",
        fromChoiceClose: opts.fromChoiceClose === true,
      }
    );
  };

  /* Cross button on choice screen → requirements gate first: when the
   * landlord's steps (TenantRequirementsFlow show-condition: any open step)
   * apply, F opens and closing it with a blocking step pending proceeds
   * without sharing; otherwise shares the displayed application as before. */
  const handleChoiceClose = async () => {
    // Website entry (cases 3-4): closing shares nothing – the request stays
    // pending for the red dot in Share profile.
    console.log("[signup-flow]", "landlord share: X closed", {
      closeBehavior: closeBehavior ?? "share",
      reqId: requestId.current ?? null,
      displayedAppId: displayedApplication?._id ?? null,
    });
    if (closeBehavior === "nothing") {
      handleClose(true, "choice-close");
      return;
    }
    if (!requestId.current) {
      handleClose(true, "choice-close");
      return;
    }
    if (displayedApplication?._id) {
      await handleApplication(displayedApplication, { fromChoiceClose: true });
      return;
    }
    await handleIndividual(true, { fromChoiceClose: true });
  };

  /* Join existing group → consent to the application + respond to landlord request */
  const handleAcceptGroup = async () => {
    if (!selectedApp || !requestId.current) return;
    const appId = selectedApp._id;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: join group selected", {
      reqId,
      appId,
      overrideMode: Boolean(requestOverride),
    });
    const doShare = async () => {
      try {
        setProcessing(true);
        const actions = [consentApplication(appId, "accepted")];
        actions.push(acceptLandlordRequest(reqId, appId));
        await Promise.all(actions);
        const userId = user?._id ?? localStorage.getItem("saveduserid");
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error("Failed to join group");
      } finally {
        setProcessing(false);
      }
    };
    await withViviaGate(
      async () => {
        await withConsentGate(doShare);
      },
      { application: selectedApp, appId, shareKind: "join-group" }
    );
  };

  /* Decline → reject the application invitation */
  const handleDeclineGroup = async () => {
    if (!selectedApp) {
      setStep("choice");
      setSelectedApp(null);
      return;
    }
    const appId = selectedApp._id;
    try {
      setProcessing(true);
      await consentApplication(appId, "rejected");
      const userId = user?._id ?? localStorage.getItem("saveduserid");
      if (userId) await loadSharedProfiles(userId, "full");
    } catch {
      toast.error("Failed to decline");
    } finally {
      setProcessing(false);
    }
    setStep("choice");
    setSelectedApp(null);
  };

  /*
   * Create group → create application first, then respond to landlord request
   * with the new application ID.
   * Consent is checked before any action — participants list is captured in closure.
   * The tenant requirements check runs after creation with the new app id, before the share.
   */
  const handleCreateAndShare = async (participants, skorProtect = true) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: create group started", {
      reqId,
      overrideMode: Boolean(requestOverride),
    });
    const createThenShare = async () => {
      try {
        const res = await createApplication({
          participants,
          title: defaultTitle,
          skor_protect: skorProtect,
        });
        const appId = res?.success_res?._id ?? res?._id;
        console.log("[signup-flow]", "landlord share: group created", {
          reqId,
          appId: appId ?? null,
          overrideMode: Boolean(requestOverride),
        });
        const doShare = async () => {
          try {
            setProcessing(true);
            await acceptLandlordRequest(reqId, appId);
            const userId = user?._id ?? localStorage.getItem("saveduserid");
            await Promise.all([
              loadApplicationsData(),
              userId ? loadSharedProfiles(userId, "full") : Promise.resolve(),
            ]);
            onDone();
          } catch (err) {
            toast.error(
              err?.response?.data?.message ??
                _t("failedToCreateGroup", "Failed to create group")
            );
            throw err;
          } finally {
            setProcessing(false);
          }
        };
        await withViviaGate(doShare, { appId, shareKind: "created-group" });
      } catch (err) {
        toast.error(
          err?.response?.data?.message ??
            _t("failedToCreateGroup", "Failed to create group")
        );
        throw err;
      }
    };
    await withConsentGate(createThenShare);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* While a share is in flight (e.g. after identification submit),
            hold a loader so the choice screen never sits interactive during
            the accept + reload + remount window. processing is always reset
            in a finally, and the consent-defer path never sets it. */}
      {landLordLoading || processing ? (
        <Modal open onClose={handleChoiceClose} dismissible={false}>
          <SkeletonList rows={3} variant="card" label={_t("loading")} />
        </Modal>
      ) : step === "choice" ? (
        <ChoiceScreen
          landlordData={landlordData}
          applicationToShare={displayedApplication}
          applicationOnly={onboardingApplicationOnly}
          hideIndividualOption={hideIndividualOption}
          onSelectIndividual={async () => {
            if (user?.skor_protect) {
              setIndividualSharing(true);
              try {
                await handleIndividual(true);
              } finally {
                setIndividualSharing(false);
              }
              return;
            }
            setStep("individual");
          }}
          onSelectGroup={(app) => {
            setSelectedApp(app);
            setStep("join-group");
          }}
          onSelectApplication={(app) => {
            // API-driven mode: the application is already decided – share it.
            if (hasApplicationOverride) {
              handleApplication(app);
              return;
            }
            const isPending = pendingAppsForLandlord.some(
              (pendingApp) => pendingApp._id === app._id
            );
            if (isPending) {
              setSelectedApp(app);
              setStep("join-group");
              return;
            }
            handleApplication(app);
          }}
          onSelectCreate={() => {
            if (typeof onCreateNew === "function") {
              onCreateNew();
              return;
            }
            setStep("create-group");
          }}
          onClose={handleChoiceClose}
          _t={_t}
          user={user}
          processing={processing}
          individualSharing={individualSharing}
        />
      ) : step === "individual" ? (
        <IndividualScreen
          landlordData={landlordData}
          onBack={() => setStep("choice")}
          onConfirm={handleIndividual}
          processing={processing}
          _t={_t}
        />
      ) : step === "join-group" ? (
        <JoinGroupScreen
          app={selectedApp}
          landlordData={landlordData}
          onBack={() => setStep("choice")}
          onClose={() => handleClose(true, "join-group-close")}
          onAccept={handleAcceptGroup}
          onDecline={handleDeclineGroup}
          processing={processing}
          user={user}
          _t={_t}
        />
      ) : (
        <CreateGroupScreen
          onBack={() => setStep("choice")}
          onClose={() => handleClose(true, "create-group-close")}
          onCreateAndShare={handleCreateAndShare}
          user={user}
        />
      )}
      {pendingVivia && (
        <BeforeSharingModal
          user={user}
          landlordName={
            landlordData?.fname_companyName?.trim() || landlordData?.email || ""
          }
          steps={pendingVivia.steps}
          memberStatuses={pendingVivia.memberStatuses}
          applicationId={pendingVivia.appId}
          applicationTitle={pendingVivia.appTitle}
          minAdults={pendingVivia.minAdults}
          household={pendingVivia.household}
          householdQuestions={pendingVivia.householdQuestions}
          applicantQuestions={pendingVivia.applicantQuestions}
          workspace={pendingVivia.workspace}
          landlordEmail={pendingVivia.landlordEmail}
          onStepDone={handleStepDone}
          onClose={handleBeforeSharingClose}
        />
      )}
    </>
  );
};

export { isSharedWithLandlord, JoinGroupScreen };
export default InvitationShareModal;
